// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/event.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { eventsApi } from "../../api/events";
import type { Event } from "../../api/types";
// override-block:imports BEGIN
import { createElement } from "react";
import type { EventStatus } from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const STATUS_LABEL: Record<EventStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  live: "Live",
  completed: "Completed",
  canceled: "Canceled",
  archived: "Archived",
};

// Status → tone token. Active states (scheduled, live) lean events (the
// "happening / temporal" gradient); positive completion lean giving;
// draft + archived lean home (neutral / inactive); canceled leans
// people (access-control / muted concern).
const STATUS_TONE: Record<EventStatus, "g-giving" | "g-events" | "g-people" | "g-home"> = {
  draft: "g-home",
  scheduled: "g-events",
  live: "g-events",
  completed: "g-giving",
  canceled: "g-people",
  archived: "g-home",
};
// override-block:helpers END

export const eventDetailConfig: DetailPageConfig<Event> = {
  idParam: "eventId",
  queryKey: (id: string) => ["events", "detail", id] as const,
  queryFn: (id: string) => eventsApi.getEvent(id),
  backTo: "/events",
  // override-block:title BEGIN
  title: (r) => r.name,
  // override-block:title END
  // override-block:subtitle BEGIN
  // INTENTIONAL DEVIATION from entity-hint subtitle_field (starts_at).
  // subtitle gets only the record (no formatters access). Raw ISO
  // starts_at would render unreadable header chrome
  // ("2026-06-04T14:00:00Z"). Project event_type (capitalized) for a
  // meaningful sub-line. The formatted start date appears prominently
  // in the "Starts" status card below, which DOES have formatters.
  subtitle: (r) =>
    r.event_type ? r.event_type[0].toUpperCase() + r.event_type.slice(1) : "",
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    {
      id: "attendees",
      label: "Attendees",
      value: r.registered_count,
      format: "number",
      tone: "g-giving",
    },
    // override-block:statcard-capacity BEGIN
    {
      id: "capacity",
      label: "Capacity",
      value: r.max_attendees ?? "—",
      format: r.max_attendees == null ? "text" : "number",
      tone: "g-home",
    },
    // override-block:statcard-capacity END
    // override-block:statcard-starts BEGIN
    {
      id: "starts",
      label: "Starts",
      value: r.starts_at,
      format: "text",
      tone: "g-mygiving",
      render: createElement(
        "span",
        { className: "tabular" },
        _fmt.formatDate(r.starts_at, "relative"),
      ),
    },
    // override-block:statcard-starts END
    // override-block:statcard-status BEGIN
    {
      id: "status",
      label: "Status",
      value: STATUS_LABEL[r.status] ?? r.status,
      format: "text",
      tone: STATUS_TONE[r.status] ?? "g-events",
    },
    // override-block:statcard-status END
  ],
  actions: (r) => [
    // override-block:actions BEGIN
    // header_actions per entity-hint.pages.detail_v2.header_actions.
    // CHG-2450 (GAP-3b-ii-b) — "Edit" now navigates to the EventEdit route
    // (/events/:id/edit) via PageHeaderAction.href (renders a <Link>); the
    // record `r` supplies the id (signature widened from `_r` to `r`). Publish
    // + Archive stay forward-promised — their workflows ship in later CHGs.
    { id: "edit", label: "Edit", variant: "secondary", href: `/events/${r.id}/edit`, permissionGate: toGate(GATE_CATALOG.event_detail["edit"], "disable") },
    { id: "publish", label: "Publish", variant: "primary", permissionGate: toGate(GATE_CATALOG.event_detail["publish"], "hide") },
    { id: "archive", label: "Archive", variant: "ghost", permissionGate: toGate(GATE_CATALOG.event_detail["archive"], "disable") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per entity-hint.pages.detail_v2.tabs. Overview = primary
    // record; Attendees + Communications bridge to forward-promised
    // sub-resources; Logistics presents derived/inline fields from the
    // primary record (location, timezone, registration_enabled, etc.).
    {
      id: "overview",
      label: "Overview",
      render: (e, fmt) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement("div", null, "Name: ", e.name),
          createElement(
            "div",
            null,
            "Type: ",
            e.event_type[0].toUpperCase() + e.event_type.slice(1),
          ),
          createElement("div", null, "Status: ", STATUS_LABEL[e.status] ?? e.status),
          createElement("div", null, "Starts: ", fmt.formatDate(e.starts_at, "medium")),
          e.ends_at
            ? createElement("div", null, "Ends: ", fmt.formatDate(e.ends_at, "medium"))
            : null,
          createElement("div", null, "Timezone: ", e.timezone ?? "—"),
          createElement("div", null, "Location: ", e.location ?? "—"),
          createElement(
            "div",
            null,
            "Visibility: ",
            e.is_public ? "Public" : "Private",
          ),
          createElement("div", null, "Description: ", e.description ?? "—"),
          createElement("div", null, "Created: ", fmt.formatDate(e.created_at, "medium")),
        ),
    },
    {
      id: "attendees",
      label: "Attendees",
      render: (_e) =>
        createElement(
          "div",
          {
            className: "card",
            style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" },
          },
          "No attendees recorded yet. (Coming soon: GET /events/{id}/attendees.)",
        ),
    },
    {
      id: "communications",
      label: "Communications",
      render: (_e) =>
        createElement(
          "div",
          {
            className: "card",
            style: { padding: 16, fontSize: 13, color: "var(--color-text-muted)" },
          },
          "No communications sent yet. (Coming soon: GET /events/{id}/delivery-logs.)",
        ),
    },
    {
      id: "logistics",
      label: "Logistics",
      render: (e, fmt) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.6 } },
          createElement(
            "div",
            null,
            "Registration: ",
            e.registration_enabled ? "Enabled" : "Disabled",
          ),
          createElement("div", null, "Registration link: ", e.registration_link ?? "—"),
          createElement(
            "div",
            null,
            "Capacity: ",
            e.max_attendees != null
              ? `${e.registered_count} of ${e.max_attendees}`
              : `${e.registered_count} (no cap)`,
          ),
          createElement("div", null, "Campaign: ", e.campaign_id ?? "—"),
          createElement("div", null, "Designation: ", e.designation_id ?? "—"),
          createElement("div", null, "Updated: ", fmt.formatDate(e.updated_at, "medium")),
        ),
    },
    // override-block:custom-tabs END
  ],
};
